import type { World } from '../sim/types';
import { SURVEY_LABELS, plainTitle } from './surveyNames';
import { ECONOMY, ROLE_NAMES, type Role, type SurveyKind } from '../sim/economy';
import { LEDGER_LINES, LEDGER_NAMES, atSea, boatJob, boatName, freeBoat, mostOf, fundsLevel, busyOn, dollars, ruleWords, surveyCost, surveyCvScale, surveyKindsAt, type Job } from '../sim/operations';
import { dateLabel, epochDayOf } from '../sim/calendar';
import { ISLAND_NAMES } from '../sim/reef';
import { escapeHtml } from './format';
import { runwayHtml, runwayWords, spareShort } from './runway';
import { coralChangeSeen } from './actionWorth';
import { researchIsland } from './goals';
import { DEFAULT_AREA_PLAN } from './areasPanel';

/**
 * The conservancy's operations as the Manage pane shows them (M238): its funds and its month, its crew and its boat,
 * the survey being planned and the work under way, each control a button (the brief's section 5: no dropdowns), each a
 * command the HUD sends (`start_survey`, `hire`, `let_go`). Markup only, from the world and the plan the HUD holds, so
 * it is tested without a browser.
 */

/**
 * The boat at sea, in a line for the chip on the view's bar (M240, the brief's section 6: the conservancy's work seen
 * while it is under way): the boat, the survey, its divers or cameras down, and the field days left; null in harbor.
 */
export function atSeaText(world: World): string | null {
  // Each boat out (M277).
  const lines = atSea(world.operations).flatMap((job) => {
    if (!job.survey) return [];
    const divers = job.staff.filter((id) => world.operations.staff.find((m) => m.id === id)?.role === 'biologist').length;
    const left = Math.max(1, (job.phaseEnds[0] as number) - epochDayOf(world));
    return [`${boatName(job.boat)}: ${SURVEY_LABELS[job.survey].toLowerCase()}, ${job.survey === 'slopeCamera' ? 'cameras down' : job.survey === 'shoreCleanup' ? 'crew on the shore' : `${divers} divers down`}, ${left} ${left === 1 ? 'day' : 'days'} left`];
  });
  return lines.length > 0 ? lines.join('; ') : null;
}

/** The survey the player is planning: its kind, its field days and its crew, held by the HUD until it is sent. */
export interface SurveyPlan {
  kind: SurveyKind;
  days: number;
  crew: number[];
}

/** What each kind counts and how, as the planner tells it. */
const KIND_WORDS: Readonly<Record<SurveyKind, string>> = {
  reefFish: 'Divers count the reef fish at random sites to 30 m, as NOAA\'s do: every reef fish the monitoring follows.',
  reefBenthic: 'Divers photograph the reef\'s bottom at random sites, as NOAA\'s do: its live coral cover.',
  reefBleaching: 'Divers photograph the reef\'s live coral at random sites and score how much of it is bleached.',
  slopeCamera: 'Cameras dropped on the deep slope, 100 to 400 m, as NOAA\'s bottomfish survey does: the Deep 7.',
  // M307: never offered among the planner's kinds (`surveyKindsAt`), told on its page while at sea.
  shoreCleanup: 'The captain and divers clear derelict nets and debris from the island\'s shore, about half a tonne a day.',
};

/** A plan's crew as the kind wants it, from those free: the captain, and two biologists (one on the slope). */
export function defaultCrew(world: World, kind: SurveyKind): number[] {
  const ops = world.operations;
  const free = (role: Role): number[] => ops.staff.filter((m) => m.role === role && !busyOn(ops, m.id)).map((m) => m.id);
  return [...free('captain').slice(0, 1), ...free('biologist').slice(0, kind === 'slopeCamera' ? 1 : 2)];
}

/** A new plan for a sanctuary: its first survey kind, ten days, its free crew. */
export function defaultPlan(world: World): SurveyPlan | null {
  const kind = surveyKindsAt(world.sanctuaries[0])[0];
  return kind ? { kind, days: 10, crew: defaultCrew(world, kind) } : null;
}

/**
 * The plan to show: the one held, its crew only those still on the staff; with none of its crew left free and the boat
 * home, the free crew for its kind; a new one where none is held.
 */
export function planFor(world: World, held: SurveyPlan | null): SurveyPlan | null {
  if (!held) return defaultPlan(world);
  const ops = world.operations;
  const crew = held.crew.filter((id) => ops.staff.some((m) => m.id === id));
  const home = freeBoat(ops) !== null && crew.every((id) => !busyOn(ops, id));
  return { ...held, crew: home && crew.length === 0 ? defaultCrew(world, held.kind) : crew };
}

/** A plan's cost, its error against NOAA's survey's, and why it cannot go, if it cannot. */
export function planCheck(world: World, plan: SurveyPlan): { cost: number; scale: number | null; why: string | null } {
  const ops = world.operations;
  const crew = plan.crew.map((id) => ops.staff.find((m) => m.id === id)).filter((m) => m !== undefined);
  const count = (role: Role): number => crew.filter((m) => m.role === role).length;
  const biologists = count('biologist');
  const cost = surveyCost(plan.kind, plan.days);
  const scale = surveyCvScale(plan.kind, plan.days, biologists, world.sanctuaries[0]);
  // No boat free, or surveys planned before it (M274, M277: the plan goes in turn).
  const job = freeBoat(ops) === null ? boatJob(ops) : null;
  const why = job ? `${ops.boats > 1 ? 'Both boats are' : `The ${ECONOMY.boat.name} is`} at sea until ${dateLabel(Math.min(...atSea(ops).map((j) => j.phaseEnds[0] as number)))}.`
    : ops.plan.length > 0 ? 'Surveys are planned before it: plan it from the reef\'s card, and it goes in its turn.'
    : plan.kind === 'reefBleaching' && plan.days > ECONOMY.assessmentDays ? `A bleaching check takes ${ECONOMY.assessmentDays} days at most: choose ${ECONOMY.assessmentDays} days.`
    : count('captain') !== 1 ? 'Choose the captain.'
    : plan.kind === 'slopeCamera' ? (biologists < 1 ? 'Choose a diver to run the cameras.' : null)
    : biologists < 2 || biologists % 2 !== 0 ? 'Divers go in pairs: choose two, or four.'
    : null;
  return { cost, scale, why: why ?? (ops.funds < cost ? `It costs ${dollars(cost)}; the conservancy has ${dollars(ops.funds)}.` : null) };
}

export { monthsInHand } from '../sim/operations';

/** A job's line: what it is and where it stands. */
function jobLine(world: World, j: Job): string {
  const today = epochDayOf(world);
  if (j.kind === 'survey' && j.survey) {
    const end = j.phaseEnds[0] as number;
    return `<li class="job"><strong>${escapeHtml(SURVEY_LABELS[j.survey])}</strong><span class="row-note">at sea, back ${escapeHtml(dateLabel(end))} (${end - today} ${end - today === 1 ? 'day' : 'days'})</span></li>`;
  }
  // Maui Nui's setup too, its job no island's (review 34: the strip counted it, the list left it out).
  if (j.kind === 'community') return `<li class="job"><strong>${escapeHtml(j.island ? ISLAND_NAMES[j.island] : 'Maui Nui')}'s volunteers</strong><span class="row-note">set up with ${j.island ? 'its community' : 'each island\'s community'} until ${escapeHtml(dateLabel(j.phaseEnds[0] as number))}</span></li>`;
  if (!j.rule) return '';
  const [consulted, made] = j.phaseEnds as [number, number];
  const stage = today < consulted ? `consulting the communities until ${dateLabel(consulted)}` : `with the State, in force about ${dateLabel(made)}`;
  return `<li class="job"><strong>${escapeHtml(ruleWords(j.rule))}</strong><span class="row-note">${escapeHtml(stage)}</span></li>`;
}

/** The operations summary at the head of Manage: funds, the months they hold, the boat, the work under way. */
export function operationsSummaryHtml(world: World): string {
  const ops = world.operations;
  const tone = ({ good: 'good', fair: 'warn', poor: 'bad' } as const)[fundsLevel(world)];
  return `<section class="ops-summary">
    <div class="ops-funds"><span class="ops-label">Funds</span><strong class="tone-${tone}">${dollars(ops.funds)}</strong><span class="row-note">${ops.funds < 0 ? 'in debt: no patrols can be paid' : `${runwayWords(world)}; ${spareShort(world)}`}</span></div>
    ${Array.from({ length: ops.boats }, (_, b) => { const j = atSea(ops).find((x) => x.boat === b); return `<div class="ops-boat"><span class="ops-label">${escapeHtml(boatName(b))}</span><span>${j?.survey ? `at sea: ${escapeHtml(SURVEY_LABELS[j.survey].toLowerCase())}, back ${escapeHtml(dateLabel(j.phaseEnds[0] as number))}` : 'in harbor, ready'}</span></div>`; }).join('')}
    <button class="primary" data-act="open" data-kind="operations">Staff and accounts</button>
  </section>`;
}

/**
 * The survey page (M262, the user's brief: "Automatically select an appropriate survey method ... assign available
 * qualified staff ... a recommended survey duration ... a simple cost and duration estimate ... one prominent Launch
 * Survey button", the rest behind "Advanced options"): the plan the HUD holds, its kind, days and crew chosen for the
 * player (`defaultPlan`), told in a sentence, its cost and its return; the planner's every choice folded below. With the
 * boat at sea, in its place the survey's progress, its return and the way to skip to it (`data-act="advance-to"`).
 */
export function surveyHtml(world: World, plan: SurveyPlan | null): string {
  const ops = world.operations;
  const job = freeBoat(ops) === null ? boatJob(ops) : null;
  const today = epochDayOf(world);
  if (job?.survey) {
    const back = job.phaseEnds[0] as number;
    const days = job.days ?? Math.max(1, back - today);
    const done = Math.min(1, Math.max(0, 1 - (back - today) / days));
    return `<h3>${escapeHtml(SURVEY_LABELS[job.survey])}</h3>
      <p class="why">${escapeHtml(KIND_WORDS[job.survey])}</p>
      <div class="progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(100 * done)}"><span style="width:${Math.round(100 * done)}%"></span></div>
      <p class="state">At sea: back ${escapeHtml(dateLabel(back))}, in ${back - today} ${back - today === 1 ? 'day' : 'days'}. ${job.survey === 'shoreCleanup' ? 'What it removed is told when it is home.' : 'Its findings open when it is home.'}</p>
      <button class="primary" data-act="advance-to" data-day="${back}" data-what="${escapeHtml(SURVEY_LABELS[job.survey])} home">Advance to its return</button>`;
  }
  const kinds = surveyKindsAt(world.sanctuaries[0]);
  if (!plan || kinds.length === 0) return '<h3>Survey</h3><p class="note">This sanctuary has no habitat its boat can survey.</p>';
  const check = planCheck(world, plan);
  const crew = plan.crew.map((id) => ops.staff.find((m) => m.id === id)).filter((m) => m !== undefined);
  const named = (role: Role): string[] => crew.filter((m) => m.role === role).map((m) => m.name);
  const who = [...named('captain').map((n) => `${n} at the helm`), named('biologist').length > 0 ? `${named('biologist').join(' and ')} ${plan.kind === 'slopeCamera' ? 'on the cameras' : 'diving'}` : ''].filter(Boolean).join(', ');
  const choices = ops.staff.filter((m) => m.role !== 'liaison').map((m) => {
    const busy = busyOn(ops, m.id);
    return `<button class="choice" data-op="plan-crew" data-id="${m.id}" aria-pressed="${plan.crew.includes(m.id)}"${busy ? ' disabled' : ''}>${escapeHtml(m.name)}<span class="row-note">${escapeHtml(ROLE_NAMES[m.role].toLowerCase())}${busy ? ', at work' : ''}</span></button>`;
  }).join('');
  const sites = plan.kind === 'slopeCamera' ? '' : `, ${plan.days * ECONOMY.reefSitesPerDay * Math.floor(named('biologist').length / 2)} sites`;
  return `<h3>${escapeHtml(SURVEY_LABELS[plan.kind])}</h3>
    <p class="why">${escapeHtml(KIND_WORDS[plan.kind])}</p>
    <dl class="plan-summary">
      <dt>Crew</dt><dd>${escapeHtml(who || 'none chosen')}</dd>
      <dt>At sea</dt><dd>${plan.days} days, back ${escapeHtml(dateLabel(today + plan.days))}</dd>
      <dt>Cost</dt><dd>${escapeHtml(dollars(check.cost))} for fuel and field costs; its data report earns a research grant of ${escapeHtml(dollars(ECONOMY.researchGrantPerSurvey))}</dd>
    </dl>
    ${check.why ? `<p class="note tone-warn">${escapeHtml(check.why)}</p>` : ''}
    <button class="primary launch" data-op="dispatch"${check.why ? ' disabled' : ''}>Launch survey</button>
    <details class="advanced"><summary>Advanced options</summary>
      <div class="choices" role="group" aria-label="Survey">${kinds.map((k) => `<button class="choice" data-op="plan-kind" data-id="${k}" aria-pressed="${k === plan.kind}">${escapeHtml(SURVEY_LABELS[k])}</button>`).join('')}</div>
      <div class="choices" role="group" aria-label="Field days">${ECONOMY.surveyDays.map((d) => `<button class="choice" data-op="plan-days" data-id="${d}" aria-pressed="${d === plan.days}">${d} days</button>`).join('')}</div>
      <div class="choices crew" role="group" aria-label="Crew">${choices}</div>
      ${check.scale === null ? '' : `<p class="note">Its counts' error about ${check.scale.toFixed(1)} times NOAA's own survey's${escapeHtml(sites)}: more days or divers, a sharper count.</p>`}
    </details>`;
}

/** A hire as the page offers it (M308, F7): the role and how many, named by what it opens. */
export interface HireOffer {
  role: Role;
  n: number;
  title: string;
  opens: string;
}

/**
 * Each hire the conservancy can still make, offered as what it opens rather than as a role (M308, the third playtest's:
 * "hiring should feel like expansion"; `docs/FIELD_LOOP_ROADMAP.md`'s F7): the divers by the pair, as they dive
 * (`biologistsFit`); a captain for each boat; a liaison for each consultation at once (`freeLiaison`).
 */
export function hireOffers(world: World): HireOffer[] {
  const ops = world.operations;
  const have = (r: Role): number => ops.staff.filter((m) => m.role === r).length;
  const out: HireOffer[] = [];
  const divers = have('biologist');
  // Its reef surveys' teams alone (`teamsOf`): the slope's cameras take one diver however many go; two surveys at sea at
  // once only with a captain for each boat (its review).
  const room = mostOf(ops, 'biologist') - divers;
  if (room > 0) {
    // Hired to the next whole pair: an odd diver dives with no one (`biologistsFit`).
    const n = Math.min(room, divers % 2 === 1 ? 1 : 2);
    const second = divers + n >= 4;
    out.push(divers < 2
      ? { role: 'biologist', n, title: divers === 0 ? 'A dive team' : 'Complete the dive team', opens: 'the reef\'s surveys go to sea again' }
      : { role: 'biologist', n, title: second && divers === 3 ? 'Complete the second dive team' : 'A second dive team', opens: `four divers on one reef survey, twice the sites in the same days${closerCount(world)}${ops.boats > 1 && have('captain') > 1 ? ', or two surveys at sea at once' : ''}` });
  }
  const captains = have('captain');
  if (captains < mostOf(ops, 'captain')) out.push(captains === 0
    ? { role: 'captain', n: 1, title: 'A boat captain', opens: 'the boat sails again' }
    : { role: 'captain', n: 1, title: 'A second captain', opens: ops.boats > 1 ? 'both boats at sea' : `both boats at sea once the ${ECONOMY.secondBoat.name} is in the water` });
  const liaisons = have('liaison');
  if (liaisons < mostOf(ops, 'liaison')) out.push(liaisons === 0
    ? { role: 'liaison', n: 1, title: 'A community liaison', opens: 'rules consulted on with the islands, and volunteers trained' }
    : { role: 'liaison', n: 1, title: 'A second liaison', opens: 'volunteers set up while the protected area is consulted on' });
  return out;
}

/**
 * What a second dive team's closer count can see (M315, `docs/OPENING_LOOP_ROADMAP.md`'s G5, the fourth playtest's:
 * "Hiring produces a 'closer count,' but its practical benefit is similarly hard to judge"): the smallest change in the
 * live coral of the island the guide would survey (`researchIsland`) a coral survey of the island card's usual days
 * (`DEFAULT_AREA_PLAN`) tells from its last count, four divers against two (`coralChangeSeen`, the survey's own error,
 * an ESTIMATE); nothing where it cannot be reckoned.
 */
function closerCount(world: World): string {
  const island = researchIsland(world);
  const [two, four] = [2, 4].map((n) => coralChangeSeen(world, island, DEFAULT_AREA_PLAN.days, n));
  if (two === null || two === undefined || four === null || four === undefined) return '';
  return ` (a change of ${Math.round(four)} points in ${ISLAND_NAMES[island]}'s coral told, not ${Math.round(two)})`;
}

/** The operations page: the work under way, the crew, and the accounts (M262: the survey's planner on its own page). */
export function operationsHtml(world: World): string {
  const ops = world.operations;
  const jobs = ops.jobs.length > 0 ? `<ul class="rows">${ops.jobs.map((j) => jobLine(world, j)).join('')}</ul>` : '<p class="note">Nothing under way.</p>';
  const staff = ops.staff.map((m) => {
    const busy = busyOn(ops, m.id);
    return `<li class="manage-row"><span class="row-name">${escapeHtml(m.name)}</span><span class="row-note">${escapeHtml(ROLE_NAMES[m.role])}, ${dollars(ECONOMY.monthlyPay[m.role])} a month${busy ? ', at work' : ''}</span><button class="choice" data-op="let-go" data-id="${m.id}"${busy ? ' disabled' : ''}>Let go</button></li>`;
  }).join('');
  const hires = hireOffers(world).map((h) => `<button data-op="hire" data-id="${h.role}" data-n="${h.n}"><strong>${escapeHtml(h.title)}</strong><span>${escapeHtml(h.opens)}; ${escapeHtml(dollars(h.n * ECONOMY.monthlyPay[h.role]))} a month more</span></button>`).join('');
  const last = ops.months.at(-1);
  const ledger = (lines: Record<string, number>): string => LEDGER_LINES.filter((l) => lines[l] !== 0).map((l) => `<dt>${escapeHtml(LEDGER_NAMES[l])}</dt><dd>${dollars(lines[l] as number)}</dd>`).join('') || '<dt>Nothing yet</dt><dd></dd>';
  const finished = ops.finished.slice(-5).reverse().map((f) => `<li><span class="row-name">${escapeHtml(plainTitle(f.title))}</span><span class="row-note">${escapeHtml(dateLabel(f.endDay))}</span></li>`).join('');
  return `<h3>Staff and accounts</h3>
    ${operationsSummaryHtml(world).replace(/<button[^]*?<\/button>/, '')}
    ${runwayHtml(world)}
    <p class="more"><button class="link" data-act="goal" data-goal="survey">Plan a survey</button></p>
    <h4>Under way</h4>
    ${jobs}
    <h4>Staff</h4>
    <ul class="rows">${staff}</ul>
    ${hires ? `<h4>Grow the team</h4><div class="home-actions">${hires}</div>` : ''}
    <h4>This month so far</h4>
    <dl class="ledger">${ledger(ops.month)}</dl>
    ${last ? `<h4>Last month</h4><dl class="ledger">${ledger(last.lines)}<dt>In hand at its end</dt><dd>${dollars(last.funds)}</dd></dl>` : ''}
    <p class="note">The operating grant comes each 1 July, the State's fiscal year. Salaries, the boat's upkeep and the patrols are paid at each month's end; tourism partners pay on the reef's coral as the monitoring last counted it.</p>
    ${finished ? `<h4>Finished</h4><ul class="rows">${finished}</ul>` : ''}`;
}

/** Each rule under way, by what it sets (`managementHtml`'s keys), told where it stands. */
export function pendingRules(world: World): Record<string, string> {
  const today = epochDayOf(world);
  return Object.fromEntries(world.operations.jobs.flatMap((j) => {
    if (!j.rule) return [];
    const key = j.rule.type === 'protect_herbivores' ? `protect-${j.rule.island}` : `limit-${j.rule.group}-${j.rule.limit}`;
    const [consulted, made] = j.phaseEnds as [number, number];
    return [[key, `Proposed: ${today < consulted ? `consulting until ${dateLabel(consulted)}` : `with the State, about ${dateLabel(made)}`}`]];
  }));
}
